import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock, Star, TrendingUp, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAccessibleCategories } from '@/hooks/useAccessibleCategories';
import { useNavigate } from 'react-router-dom';
import { categoryConfigs } from '@/config/categories';

interface SearchResult {
  id: string;
  name: string;
  description: string;
  type: 'category' | 'session' | 'recent';
  category?: string;
  score: number;
}

interface SearchBarProps {
  placeholder?: string;
  className?: string;
  compact?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = "Search wellness sessions, categories...",
  className,
  compact = false
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { accessibleCategories } = useAccessibleCategories();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  // Save recent searches
  const addToRecentSearches = (searchTerm: string) => {
    const updated = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Search function
  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    
    // Simulate search delay for better UX
    await new Promise(resolve => setTimeout(resolve, 200));

    const searchResults: SearchResult[] = [];
    const lowerQuery = searchQuery.toLowerCase();

    // Search categories
    accessibleCategories.forEach(category => {
      const nameMatch = category.name.toLowerCase().includes(lowerQuery);
      const descMatch = category.description.toLowerCase().includes(lowerQuery);
      
      if (nameMatch || descMatch) {
        searchResults.push({
          id: category.id,
          name: category.name,
          description: category.description,
          type: 'category',
          score: nameMatch ? 10 : 5
        });
      }
    });

    // Add popular suggestions based on query
    const suggestions = getSmartSuggestions(lowerQuery);
    searchResults.push(...suggestions);

    // Sort by relevance score
    searchResults.sort((a, b) => b.score - a.score);

    setResults(searchResults.slice(0, 8));
    setIsLoading(false);
  };

  // Smart suggestions based on keywords
  const getSmartSuggestions = (query: string): SearchResult[] => {
    const suggestions: SearchResult[] = [];
    
    const keywordMap: Record<string, string[]> = {
      'stress': ['anxiety', 'meditation', 'be_still'],
      'sleep': ['sleep', 'meditation'],
      'focus': ['meditation', 'memory', 'motivation'],
      'relax': ['meditation', 'massage', 'anxiety'],
      'energy': ['motivation', 'fitness'],
      'calm': ['meditation', 'anxiety', 'be_still'],
      'pain': ['massage', 'meditation'],
      'love': ['self_love', 'meditation'],
      'morning': ['motivation', 'meditation'],
      'evening': ['sleep', 'meditation'],
      'workout': ['fitness', 'motivation'],
      'mindful': ['meditation', 'be_still']
    };

    Object.entries(keywordMap).forEach(([keyword, categoryIds]) => {
      if (keyword.includes(query) || query.includes(keyword)) {
        categoryIds.forEach(categoryId => {
          const category = categoryConfigs[categoryId];
          if (category && accessibleCategories.some(c => c.id === categoryId)) {
            suggestions.push({
              id: categoryId,
              name: category.name,
              description: `Perfect for ${keyword}`,
              type: 'session',
              category: categoryId,
              score: 7
            });
          }
        });
      }
    });

    return suggestions;
  };

  // Handle search input changes
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (query) {
        performSearch(query);
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [query, accessibleCategories]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (result: SearchResult) => {
    addToRecentSearches(result.name);
    setIsOpen(false);
    setQuery('');
    
    if (result.type === 'category' || result.type === 'session') {
      navigate(`/category/${result.id}`);
    }
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  return (
    <div ref={searchRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={cn(
            'pl-10 pr-10 rounded-xl border-border/50 focus:border-primary/50 focus:ring-primary/20',
            compact ? 'h-9 text-sm' : 'h-11'
          )}
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 h-6 w-6"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 max-h-96 overflow-y-auto shadow-depth-3">
          <CardContent className="p-0">
            {/* Recent Searches */}
            {!query && recentSearches.length > 0 && (
              <div className="p-4 border-b border-border/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Recent Searches
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearRecentSearches}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map((search, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer hover:bg-primary/10"
                      onClick={() => setQuery(search)}
                    >
                      {search}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {query && (
              <div className="p-2">
                {isLoading ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <Search className="h-4 w-4 animate-pulse" />
                      Searching...
                    </div>
                  </div>
                ) : results.length > 0 ? (
                  <div className="space-y-1">
                    {results.map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className="w-full p-3 text-left hover:bg-muted/50 rounded-lg transition-colors flex items-start gap-3 group"
                      >
                        <div className="flex-shrink-0 mt-1">
                          {result.type === 'category' && <Star className="h-4 w-4 text-primary" />}
                          {result.type === 'session' && <TrendingUp className="h-4 w-4 text-secondary" />}
                          {result.type === 'recent' && <Clock className="h-4 w-4 text-muted-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm group-hover:text-primary transition-colors">
                            {result.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {result.description}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {result.type}
                        </Badge>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    <div className="space-y-2">
                      <Filter className="h-8 w-8 mx-auto opacity-50" />
                      <p className="text-sm">No results found for "{query}"</p>
                      <p className="text-xs">Try searching for meditation, sleep, or stress relief</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            {!query && (
              <div className="p-4 border-t border-border/50">
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Popular Categories
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {accessibleCategories.slice(0, 4).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleResultClick({
                        id: category.id,
                        name: category.name,
                        description: category.description,
                        type: 'category',
                        score: 10
                      })}
                      className="p-2 text-left hover:bg-muted/50 rounded-lg transition-colors text-sm"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchBar;